import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Loader2, Check, MapPin } from 'lucide-react';
import { CoverImageUpload } from '@/components/CoverImageUpload';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { BrutalTextarea } from '@/components/ui/BrutalTextarea';
import { SimpleRichTextEditor } from '@/components/editor/SimpleRichTextEditor';
import { sanitizeText, sanitizeRichText, sanitizeUrl } from '@/lib/sanitize';
import { BrutalCard } from '@/components/ui/BrutalCard';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { AddressPreviewMap } from '@/components/AddressPreviewMap';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const STEPS_CONFIG = [
  { id: 1, labelKey: 'events.steps.info', descriptionKey: 'events.steps.infoDesc' },
  { id: 2, labelKey: 'events.steps.type', descriptionKey: 'events.steps.typeDesc' },
  { id: 3, labelKey: 'events.steps.where', descriptionKey: 'events.steps.whereDesc' },
  { id: 4, labelKey: 'events.steps.summary', descriptionKey: 'events.steps.summaryDesc' },
];

const EventCreate = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description_it: '',
    description_en: '',
    description_es: '',
    event_type: 'free' as 'free' | 'paid_external',
    event_format: 'in_person' as 'in_person' | 'online' | 'hybrid',
    event_datetime: '',
    is_multi_day: false,
    end_datetime: '',
    timezone: 'Europe/Rome',
    location_name: '',
    location_address: '',
    location_city: '',
    location_url: '',
    location_latitude: null as number | null,
    location_longitude: null as number | null,
    max_participants: '',
    requires_approval: false,
    external_ticket_url: '',
    external_platform: '',
    price: '',
    currency: 'EUR',
    cover_image_url: '',
  });

  // Load existing event data when in edit mode
  useEffect(() => {
    if (id && user) {
      loadEvent(id);
    }
  }, [id, user]);

  const loadEvent = async (eventId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .eq('organizer_id', user!.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        toast({
          title: t('common.error'),
          description: t('events.eventNotFound'),
          variant: 'error',
        });
        navigate('/dashboard/events');
        return;
      }

      // Format datetime for input fields
      const formatDatetime = (dt: string | null) => {
        if (!dt) return '';
        return dt.slice(0, 16); // Format: YYYY-MM-DDTHH:MM
      };

      // Detect if event is multi-day (start and end dates are different)
      const startFormatted = formatDatetime(data.start_datetime);
      const endFormatted = formatDatetime(data.end_datetime);
      const isMultiDay = startFormatted !== endFormatted && !!endFormatted;

      setFormData({
        title: data.title || '',
        description_it: data.description_it || '',
        description_en: data.description_en || '',
        description_es: data.description_es || '',
        event_type: (data.event_type as 'free' | 'paid_external') || 'free',
        event_format: (data.event_format as 'in_person' | 'online' | 'hybrid') || 'in_person',
        event_datetime: startFormatted,
        is_multi_day: isMultiDay,
        end_datetime: isMultiDay ? endFormatted : '',
        timezone: data.timezone || 'Europe/Rome',
        location_name: data.location_name || '',
        location_address: data.location_address || '',
        location_city: data.location_city || '',
        location_url: data.location_url || '',
        location_latitude: data.location_latitude || null,
        location_longitude: data.location_longitude || null,
        max_participants: data.max_participants?.toString() || '',
        requires_approval: data.requires_approval || false,
        external_ticket_url: data.external_ticket_url || '',
        external_platform: data.external_platform || '',
        price: data.price?.toString() || '',
        currency: data.currency || 'EUR',
        cover_image_url: data.cover_image_url || '',
      });
    } catch (error: any) {
      console.error('Error loading event:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSave = async (status: 'draft' | 'published') => {
    if (!user) return;

    if (!isPro()) {
      toast({
        title: t('events.subscriptionRequired'),
        description: t('events.subscriptionRequiredDesc'),
        variant: 'error',
      });
      navigate('/pricing');
      return;
    }

    setSaving(true);
    try {
      const slug = generateSlug(formData.title);

      // Sanitize all input fields before saving
      const sanitizedTitle = sanitizeText(formData.title);
      const sanitizedDescIt = sanitizeRichText(formData.description_it);
      const sanitizedDescEn = sanitizeRichText(formData.description_en);
      const sanitizedDescEs = sanitizeRichText(formData.description_es);
      const sanitizedLocationName = sanitizeText(formData.location_name);
      const sanitizedLocationAddress = sanitizeText(formData.location_address);
      const sanitizedLocationCity = sanitizeText(formData.location_city);
      const sanitizedLocationUrl = sanitizeUrl(formData.location_url);
      const sanitizedTicketUrl = sanitizeUrl(formData.external_ticket_url);
      const sanitizedPlatform = sanitizeText(formData.external_platform);

      const eventData: any = {
        title: sanitizedTitle,
        slug,
        description_it: sanitizedDescIt,
        description_en: sanitizedDescEn,
        description_es: sanitizedDescEs,
        event_type: formData.event_type,
        event_format: formData.event_format,
        start_datetime: formData.event_datetime,
        end_datetime: formData.is_multi_day ? formData.end_datetime : formData.event_datetime,
        timezone: formData.timezone,
        status,
      };

      if (formData.event_format !== 'online') {
        eventData.location_name = sanitizedLocationName;
        eventData.location_address = sanitizedLocationAddress;
        eventData.location_city = sanitizedLocationCity;
        eventData.location_latitude = formData.location_latitude;
        eventData.location_longitude = formData.location_longitude;
      }

      if (formData.event_format !== 'in_person') {
        eventData.location_url = sanitizedLocationUrl;
      }

      if (formData.event_type === 'free') {
        if (formData.max_participants) {
          eventData.max_participants = parseInt(formData.max_participants);
        }
        eventData.requires_approval = formData.requires_approval;
      } else {
        eventData.external_ticket_url = sanitizedTicketUrl;
        eventData.external_platform = sanitizedPlatform;
        eventData.price = parseFloat(formData.price);
        eventData.currency = formData.currency;
      }

      if (formData.cover_image_url) {
        eventData.cover_image_url = formData.cover_image_url;
      }

      let error;

      if (isEditMode) {
        // Update existing event
        const result = await supabase
          .from('events')
          .update(eventData)
          .eq('id', id)
          .eq('organizer_id', user.id);
        error = result.error;
      } else {
        // Create new event
        eventData.organizer_id = user.id;
        const result = await supabase.from('events').insert(eventData);
        error = result.error;
      }

      if (error) throw error;

      // Invalidate events cache so new event appears immediately
      queryClient.invalidateQueries({ queryKey: ['events'] });

      toast({
        title: isEditMode ? t('events.eventUpdated') : t('events.eventCreated'),
        description: isEditMode ? t('events.eventUpdatedDesc') : t('events.eventCreatedDesc'),
      });

      navigate('/dashboard/events');
    } catch (error: any) {
      console.error('Error saving event:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const canGoNext = () => {
    if (step === 1) {
      return formData.title && formData.description_it;
    }
    if (step === 2) {
      if (formData.event_type === 'free') return true;
      return formData.external_ticket_url && formData.price;
    }
    if (step === 3) {
      if (!formData.event_datetime) return false;
      // If multi-day, validate end_datetime exists and is >= event_datetime
      if (formData.is_multi_day) {
        if (!formData.end_datetime || formData.end_datetime < formData.event_datetime) {
          return false;
        }
      }
      if (formData.event_format === 'in_person') {
        return !!formData.location_city;
      }
      if (formData.event_format === 'online') {
        return !!formData.location_url;
      }
      return !!formData.location_city && !!formData.location_url;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard/events">{t('events.myEvents')}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {isEditMode ? t('events.editEvent') : t('events.createEvent')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard/events")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-3xl font-bold text-dark">
            {isEditMode ? t('events.editEvent') : t('events.createEvent')}
          </h1>
        </div>
        <p className="text-muted-foreground mb-8 ml-14">
          {isEditMode ? t('events.editEventDesc') : t('events.createEventDesc')}
        </p>

        {/* Form Content - centered with max width for readability */}
        <div className="max-w-3xl mx-auto">

        {/* Current Step Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-heading font-bold text-foreground uppercase tracking-wide">
            {t(STEPS_CONFIG[step - 1].labelKey)}
          </h2>
          <p className="text-muted-foreground font-body text-sm mt-1">
            {t(STEPS_CONFIG[step - 1].descriptionKey)}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-start mb-8 px-2">
          {STEPS_CONFIG.map((s, index) => {
            const isCompleted = step > s.id;
            const isActive = step === s.id;
            
            return (
              <div key={s.id} className={`flex flex-col items-start ${index < STEPS_CONFIG.length - 1 ? 'flex-1' : ''}`}>
                <div className="flex items-center w-full">
                  {/* Step Circle */}
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 border-[3px] border-dark flex items-center justify-center shadow-[3px_3px_0px_0px] shadow-dark transition-colors
                    ${isCompleted ? 'bg-success text-success-foreground' : isActive ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'}`}>
                    {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <span className="font-heading font-bold text-lg sm:text-xl">{s.id}</span>}
                  </div>
                  {/* Connector Line */}
                  {index < STEPS_CONFIG.length - 1 && (
                    <div className={`flex-1 h-1 border-t-[3px] mx-1 sm:mx-2 transition-colors
                      ${step > s.id ? 'border-success' : 'border-dark/30'}`} />
                  )}
                </div>
                {/* Step Label */}
                <span className={`text-[9px] sm:text-xs font-heading mt-2 text-left uppercase tracking-wide leading-tight w-full pr-2
                  ${isActive ? 'text-primary font-bold' : isCompleted ? 'text-success font-medium' : 'text-muted-foreground'}`}>
                  {t(s.labelKey)}
                </span>
              </div>
            );
          })}
        </div>

        <BrutalCard>
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-heading font-bold text-dark uppercase text-sm">{t('events.title')}</Label>
                <BrutalInput
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label className="font-heading font-bold text-dark uppercase text-sm">{t('events.description')}</Label>
                <Tabs defaultValue="it" className="w-full">
                  <TabsList className="mb-4 border-[2px] border-dark bg-background">
                    <TabsTrigger value="it" className="gap-2">
                      🇮🇹 <span className="hidden sm:inline">Italiano</span> *
                      {formData.description_it && <span className="text-xs text-primary">✓</span>}
                    </TabsTrigger>
                    <TabsTrigger value="en" className="gap-2">
                      🇬🇧 <span className="hidden sm:inline">English</span>
                      {formData.description_en && <span className="text-xs text-primary">✓</span>}
                    </TabsTrigger>
                    <TabsTrigger value="es" className="gap-2">
                      🇪🇸 <span className="hidden sm:inline">Español</span>
                      {formData.description_es && <span className="text-xs text-primary">✓</span>}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="it">
                    <SimpleRichTextEditor
                      value={formData.description_it}
                      onChange={(html) => setFormData({ ...formData, description_it: html })}
                      placeholder={t('events.descriptionPlaceholder')}
                    />
                  </TabsContent>

                  <TabsContent value="en">
                    <SimpleRichTextEditor
                      value={formData.description_en}
                      onChange={(html) => setFormData({ ...formData, description_en: html })}
                      placeholder={t('events.descriptionPlaceholder')}
                    />
                  </TabsContent>

                  <TabsContent value="es">
                    <SimpleRichTextEditor
                      value={formData.description_es}
                      onChange={(html) => setFormData({ ...formData, description_es: html })}
                      placeholder={t('events.descriptionPlaceholder')}
                    />
                  </TabsContent>
                </Tabs>
              </div>

              {/* Cover Image */}
              {user && (
                <div className="space-y-2">
                  <Label className="font-heading font-bold text-dark uppercase text-sm">
                    {t('events.coverImage')}
                  </Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('events.coverImageHint')}
                  </p>
                  <CoverImageUpload
                    userId={user.id}
                    currentImage={formData.cover_image_url}
                    onImageUploaded={(url) => setFormData({ ...formData, cover_image_url: url })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="font-heading font-bold text-dark uppercase text-sm">{t('events.eventType')}</Label>
                <RadioGroup
                  value={formData.event_type}
                  onValueChange={(v: any) => setFormData({ ...formData, event_type: v })}
                >
                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card cursor-pointer hover:bg-primary/10 transition-colors">
                    <RadioGroupItem value="free" id="free" className="border-[3px] border-dark" />
                    <Label htmlFor="free" className="font-body font-semibold cursor-pointer flex-1">{t('events.freeEvent')}</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card cursor-pointer hover:bg-primary/10 transition-colors">
                    <RadioGroupItem value="paid_external" id="paid_external" className="border-[3px] border-dark" />
                    <Label htmlFor="paid_external" className="font-body font-semibold cursor-pointer flex-1">{t('events.paidExternal')}</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.event_type === 'free' ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="max_participants" className="font-heading font-bold text-dark uppercase text-sm">{t('events.maxParticipants')}</Label>
                    <BrutalInput
                      id="max_participants"
                      type="number"
                      value={formData.max_participants}
                      onChange={(e) =>
                        setFormData({ ...formData, max_participants: e.target.value })
                      }
                      placeholder={t('events.unlimited')}
                    />
                  </div>

                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card">
                    <Checkbox
                      id="requires_approval"
                      checked={formData.requires_approval}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, requires_approval: !!checked })
                      }
                      className="border-[3px] border-dark data-[state=checked]:bg-primary"
                    />
                    <Label htmlFor="requires_approval" className="font-body font-semibold">{t('events.requiresApproval')}</Label>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="price" className="font-heading font-bold text-dark uppercase text-sm">{t('events.price')}</Label>
                    <BrutalInput
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="external_ticket_url" className="font-heading font-bold text-dark uppercase text-sm">{t('events.externalTicketUrl')}</Label>
                    <BrutalInput
                      id="external_ticket_url"
                      type="url"
                      value={formData.external_ticket_url}
                      onChange={(e) =>
                        setFormData({ ...formData, external_ticket_url: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="platform" className="font-heading font-bold text-dark uppercase text-sm">{t('events.platform')}</Label>
                    <Select
                      value={formData.external_platform}
                      onValueChange={(v) => setFormData({ ...formData, external_platform: v })}
                    >
                      <SelectTrigger className="border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark bg-card font-body">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark">
                        <SelectItem value="Eventbrite" className="font-body hover:bg-primary/20">Eventbrite</SelectItem>
                        <SelectItem value="Meetup" className="font-body hover:bg-primary/20">Meetup</SelectItem>
                        <SelectItem value="Altro" className="font-body hover:bg-primary/20">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="event_datetime" className="font-heading font-bold text-dark uppercase text-sm">{t('events.eventDate')}</Label>
                <BrutalInput
                  id="event_datetime"
                  type="datetime-local"
                  value={formData.event_datetime}
                  onChange={(e) => setFormData({ ...formData, event_datetime: e.target.value })}
                />
              </div>

              {/* Multi-day Switch */}
              <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card">
                <Switch
                  id="is_multi_day"
                  checked={formData.is_multi_day}
                  onCheckedChange={(checked) => {
                    setFormData({ 
                      ...formData, 
                      is_multi_day: checked,
                      end_datetime: checked ? formData.event_datetime : ''
                    });
                  }}
                />
                <Label htmlFor="is_multi_day" className="font-body font-semibold cursor-pointer">
                  {t('events.multiDayEvent')}
                </Label>
              </div>

              {/* End Date (only if multi-day ON) */}
              {formData.is_multi_day && (
                <div className="space-y-2">
                  <Label htmlFor="end_datetime" className="font-heading font-bold text-dark uppercase text-sm">
                    {t('events.endDate')}
                  </Label>
                  <BrutalInput
                    id="end_datetime"
                    type="datetime-local"
                    value={formData.end_datetime}
                    min={formData.event_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                  />
                </div>
              )}

              <div className="space-y-4">
                <Label className="font-heading font-bold text-dark uppercase text-sm">{t('events.format')}</Label>
                <RadioGroup
                  value={formData.event_format}
                  onValueChange={(v: any) => setFormData({ ...formData, event_format: v })}
                >
                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card cursor-pointer hover:bg-primary/10 transition-colors">
                    <RadioGroupItem value="in_person" id="in_person" className="border-[3px] border-dark" />
                    <Label htmlFor="in_person" className="font-body font-semibold cursor-pointer flex-1">{t('events.inPerson')}</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card cursor-pointer hover:bg-primary/10 transition-colors">
                    <RadioGroupItem value="online" id="online" className="border-[3px] border-dark" />
                    <Label htmlFor="online" className="font-body font-semibold cursor-pointer flex-1">{t('events.online')}</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border-[3px] border-dark bg-card cursor-pointer hover:bg-primary/10 transition-colors">
                    <RadioGroupItem value="hybrid" id="hybrid" className="border-[3px] border-dark" />
                    <Label htmlFor="hybrid" className="font-body font-semibold cursor-pointer flex-1">{t('events.hybrid')}</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.event_format !== 'online' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="location_name" className="font-heading font-bold text-dark uppercase text-sm">{t('events.locationName')}</Label>
                    <BrutalInput
                      id="location_name"
                      value={formData.location_name}
                      onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location_address" className="font-heading font-bold text-dark uppercase text-sm">{t('events.address')}</Label>
                    <BrutalInput
                      id="location_address"
                      value={formData.location_address}
                      onChange={(e) =>
                        setFormData({ ...formData, location_address: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location_city" className="font-heading font-bold text-dark uppercase text-sm">{t('events.city')}</Label>
                    <BrutalInput
                      id="location_city"
                      value={formData.location_city}
                      onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                    />
                  </div>

                  {/* Geocoding and Map */}
                  {formData.location_city && (
                    <div className="space-y-4">
                      <BrutalButton
                        type="button"
                        variant="secondary"
                        onClick={async () => {
                          if (!formData.location_city) return;
                          
                          setGeocoding(true);
                          try {
                            const { data, error } = await supabase.functions.invoke('geocode-address', {
                              body: { 
                                location_name: formData.location_name || undefined,
                                address: formData.location_address || undefined,
                                city: formData.location_city,
                                country: 'Italy'
                              }
                            });
                            
                            if (error) throw error;
                            
                            if (data?.latitude && data?.longitude) {
                              setFormData(prev => ({
                                ...prev,
                                location_latitude: data.latitude,
                                location_longitude: data.longitude,
                              }));
                              
                              // Toast based on precision level
                              switch (data.precision) {
                                case 'exact':
                                  toast({
                                    title: t('events.positionFoundExact'),
                                    description: t('events.positionFoundExactDesc'),
                                  });
                                  break;
                                case 'address':
                                  toast({
                                    title: t('events.positionFoundAddress'),
                                    description: t('events.positionFoundAddressDesc'),
                                  });
                                  break;
                                case 'city':
                                  toast({
                                    title: t('events.positionFoundCity'),
                                    description: t('events.positionFoundCityDesc'),
                                    variant: 'warning',
                                  });
                                  break;
                                default:
                                  toast({
                                    title: t('events.positionFound'),
                                    description: t('events.dragMarkerToAdjust'),
                                  });
                              }
                            } else {
                              toast({
                                title: t('events.positionNotFound'),
                                description: t('events.positionNotFoundDesc'),
                                variant: 'error',
                              });
                            }
                          } catch (error: any) {
                            console.error('Geocode error:', error);
                            toast({
                              title: t('common.error'),
                              description: t('events.geocodeError'),
                              variant: 'error',
                            });
                          } finally {
                            setGeocoding(false);
                          }
                        }}
                        disabled={geocoding}
                        className="w-full"
                      >
                        {geocoding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                        {t('events.verifyPosition')}
                      </BrutalButton>

                      {formData.location_latitude && formData.location_longitude && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">
                            {t('events.dragMarkerToAdjust')}
                          </p>
                          <AddressPreviewMap
                            latitude={formData.location_latitude}
                            longitude={formData.location_longitude}
                            displayName={formData.location_name || formData.location_city}
                            onPositionChange={(lat, lng) => {
                              setFormData(prev => ({
                                ...prev,
                                location_latitude: lat,
                                location_longitude: lng,
                              }));
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {formData.event_format !== 'in_person' && (
                <div className="space-y-2">
                  <Label htmlFor="location_url" className="font-heading font-bold text-dark uppercase text-sm">{t('events.meetingUrl')}</Label>
                  <BrutalInput
                    id="location_url"
                    type="url"
                    value={formData.location_url}
                    onChange={(e) => setFormData({ ...formData, location_url: e.target.value })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 4 - Summary */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-xl font-heading font-bold text-dark uppercase">{t('events.summary')}</h3>
              
              {/* Cover Image Preview */}
              {formData.cover_image_url && (
                <div className="mb-4">
                  <span className="font-heading font-bold text-dark uppercase text-sm block mb-2">
                    {t('events.coverImage')}:
                  </span>
                  <div className="w-full h-[150px] border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark overflow-hidden">
                    <img
                      src={formData.cover_image_url}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="p-3 border-l-[4px] border-primary bg-card border-[3px] border-dark shadow-[2px_2px_0px_0px] shadow-dark">
                  <span className="font-heading font-bold text-dark uppercase text-sm">{t('events.title')}:</span>{' '}
                  <span className="font-body">{formData.title}</span>
                </div>
                <div className="p-3 border-l-[4px] border-primary bg-card border-[3px] border-dark shadow-[2px_2px_0px_0px] shadow-dark">
                  <span className="font-heading font-bold text-dark uppercase text-sm">{t('events.eventType')}:</span>{' '}
                  <span className="font-body">{formData.event_type === 'free' ? t('events.free') : t('events.paid')}</span>
                </div>
                <div className="p-3 border-l-[4px] border-primary bg-card border-[3px] border-dark shadow-[2px_2px_0px_0px] shadow-dark">
                  <span className="font-heading font-bold text-dark uppercase text-sm">{t('events.format')}:</span>{' '}
                  <span className="font-body">
                    {formData.event_format === 'in_person'
                      ? t('events.inPerson')
                      : formData.event_format === 'online'
                      ? t('events.online')
                      : t('events.hybrid')}
                  </span>
                </div>
                <div className="p-3 border-l-[4px] border-primary bg-card border-[3px] border-dark shadow-[2px_2px_0px_0px] shadow-dark">
                  <span className="font-heading font-bold text-dark uppercase text-sm">{t('events.eventDate')}:</span>{' '}
                  <span className="font-body">
                    {formData.event_datetime}
                    {formData.is_multi_day && formData.end_datetime && (
                      <> → {formData.end_datetime}</>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

        </BrutalCard>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          {step > 1 ? (
            <BrutalButton variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back')}
            </BrutalButton>
          ) : (
            <div />
          )}

          {step < 4 ? (
            <BrutalButton 
              variant="primary"
              onClick={() => setStep(step + 1)} 
              disabled={!canGoNext()}
            >
              {t('common.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </BrutalButton>
          ) : (
            <div className="flex gap-3">
              <BrutalButton
                variant="secondary"
                onClick={() => handleSave('draft')}
                disabled={saving}
              >
                {t('events.saveDraft')}
              </BrutalButton>
              <BrutalButton 
                variant="primary"
                onClick={() => handleSave('published')} 
                disabled={saving}
              >
                {t('events.publish')}
              </BrutalButton>
            </div>
          )}
        </div>
        </div>
      </main>
    </div>
  );
};

export default EventCreate;
